import { makeStyles } from "tss-react/mui"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { Box, CircularProgress, Grid, Stack, Typography } from "@mui/material"
import { GuestData } from "../../../external/brotherPrinter"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { useEffect } from "react"
import { enqueueSnackbar } from "notistack"
import { useLabelPrinter } from "../../hooks/useLabelPrinter"

const useStyles = makeStyles()(() => ({
    modal: {
        padding: "3rem",
    },
    printButton: {
        padding: ".5rem 3rem !important",
    },
    label: {
        border: "1px solid #CBD5E1",
        borderRadius: "0.75rem",
        width: "100%",
        padding: "0.625rem 0.75rem ",
    },
}))

type PrintLabelsModalProps = {
    labels: Array<GuestData>
    open: boolean
    onClose: () => void
}

export function PrintLabelsModal({ open, onClose, labels }: PrintLabelsModalProps) {
    const { classes } = useStyles()

    const {
        printMultipleGuestLabels,
        hasError,
        printErrorMessage,
        isLoading,
        currentLabelIndex,
        totalLabels,
        cancelPrinting,
    } = useLabelPrinter()

    useEffect(() => {
        if (hasError && printErrorMessage) {
            enqueueSnackbar({
                variant: "error",
                message: printErrorMessage,
            })
        }
    }, [hasError, printErrorMessage])

    const handlePrint = async () => {
        await printMultipleGuestLabels(labels)
        onClose()
    }

    const handleCancelPrint = () => {
        cancelPrinting()
    }

    return (
        <StandardDialog
            dialogTitle={
                <LocalizedStrict
                    id={
                        isLoading
                            ? "service-center-reports-dashboard-printing-labels-modal-title"
                            : "service-center-reports-dashboard-print-labels-modal-title"
                    }
                >
                    <>Print guest labels</>
                </LocalizedStrict>
            }
            open={open}
            onClose={onClose}
            maxWidthProp={isLoading ? "sm" : "md"}
            scroll="body"
        >
            <Box className={classes.modal}>
                {isLoading ? (
                    <Stack direction="column" alignItems="center" spacing="2rem">
                        <Typography variant="h6" color="textSecondary">
                            {currentLabelIndex}
                            <LocalizedStrict id="service-center-reports-dashboard-printing-labels-of">
                                <>of</>
                            </LocalizedStrict>
                            {totalLabels}
                        </Typography>
                        <CircularProgress size={70} />
                        <LocalizedStrict id="service-center-reports-dashboard-printing-labels-cancel">
                            <StandardButton
                                className={classes.printButton}
                                fullWidth={false}
                                onClick={handleCancelPrint}
                            >
                                Cancel
                            </StandardButton>
                        </LocalizedStrict>
                    </Stack>
                ) : (
                    <>
                        <LocalizedStrict
                            id="service-center-reports-dashboard-printing-labels-modal-subtitle"
                            vars={{ amount: labels.length }}
                        >
                            <Typography variant="h6" mb="2rem">
                                Guest labels to print ({labels.length})
                            </Typography>
                        </LocalizedStrict>
                        <Stack direction="column" spacing="1rem" mb="3rem">
                            {labels.map((label, index) => (
                                <Grid key={index} container gap=".5rem">
                                    <Grid item xs={12} md={5.9}>
                                        <Typography className={classes.label}>{label.visitorName}</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={5.9}>
                                        <Typography className={classes.label}>{label.visitorCompany}</Typography>
                                    </Grid>
                                </Grid>
                            ))}
                        </Stack>

                        <Stack alignItems="center" justifyContent="center">
                            <LocalizedStrict id="service-center-reports-dashboard-printing-labels-print">
                                <StandardButton
                                    className={classes.printButton}
                                    fullWidth={false}
                                    onClick={handlePrint}
                                    disabled={isLoading || labels.length === 0}
                                >
                                    Print
                                </StandardButton>
                            </LocalizedStrict>
                        </Stack>
                    </>
                )}
            </Box>
        </StandardDialog>
    )
}
